/**
 * Store Deliveries Mapper
 * تحويل كائنات نقل البيانات إلى نماذج
 * Supports spec shape (id, created_at, action_type store_delivery|marketer_sale) and legacy shape.
 */

import {
  StoreDeliverySummaryResponseDTO,
  StoreDeliveryTimelineResponseDTO,
  StoreDeliveryTransactionResponseDTO,
  StoreDeliveryItemDTO,
  StoreDeliveryTransactionDTO,
  StoreDeliveryTransactionSpecDTO,
  StoreDeliverySummaryDTO
} from '../models/storeDeliveries.dto';
import {
  StoreDeliverySummary,
  StoreDeliveryTransaction,
  StoreDeliveryItem
} from '../models/storeDeliveries.model';

function parseCreatedAt(createdAt: string): { date: string; time: string } {
  try {
    const d = new Date(createdAt);
    const date = d.toISOString().slice(0, 10);
    const time = d.toTimeString().slice(0, 8);
    return { date, time };
  } catch {
    return { date: '', time: '' };
  }
}

function mapActionType(actionType: string): 'delivery' | 'return' | 'adjustment' {
  const s = (actionType || '').toLowerCase();
  if (s === 'return' || s.includes('return')) return 'return';
  if (s === 'adjustment' || s.includes('adjustment')) return 'adjustment';
  return 'delivery';
}

export const mapDeliveryItemDTOToModel = (dto: StoreDeliveryItemDTO): StoreDeliveryItem => {
  const serialNumbers = dto.serial_numbers ?? (dto.serial_number ? [dto.serial_number] : undefined);
  const hasSerial = dto.has_serial ?? dto.is_serialized ?? false;
  return {
    productId: dto.product_id,
    productName: dto.product_name,
    productBarcode: dto.product_barcode ?? '',
    quantity: dto.quantity,
    serialNumbers,
    hasSerial
  };
};

type AnyTransactionDTO = StoreDeliveryTransactionDTO | StoreDeliveryTransactionSpecDTO;

function isSpecTransaction(dto: AnyTransactionDTO): dto is StoreDeliveryTransactionSpecDTO {
  return 'id' in dto && typeof (dto as StoreDeliveryTransactionSpecDTO).id === 'number' && 'created_at' in dto;
}

export const mapDeliveryTransactionDTOToModel = (dto: AnyTransactionDTO): StoreDeliveryTransaction => {
  if (isSpecTransaction(dto)) {
    const { date, time } = parseCreatedAt(dto.created_at);
    const items = dto.items ?? [];
    const totalQuantity = items.reduce((sum, i) => sum + (i.quantity ?? 0), 0);
    return {
      transactionId: String(dto.id),
      date,
      time,
      actionType: mapActionType(dto.action_type),
      sourceType: dto.source_type === 'warehouse' ? 'warehouse' : dto.source_type === 'marketer' ? 'marketer' : 'warehouse',
      sourceId: dto.source_id,
      sourceName: undefined,
      invoiceId: dto.invoice_id ?? undefined,
      invoiceNumber: undefined,
      itemsCount: items.length,
      totalQuantity,
      items: items.map(mapDeliveryItemDTOToModel)
    };
  }
  const legacy = dto as StoreDeliveryTransactionDTO;
  return {
    transactionId: legacy.transaction_id ?? String(legacy.id ?? ''),
    date: legacy.date ?? parseCreatedAt(legacy.created_at ?? '').date,
    time: legacy.time ?? parseCreatedAt(legacy.created_at ?? '').time,
    actionType: mapActionType(legacy.action_type),
    sourceType: legacy.source_type,
    sourceId: legacy.source_id,
    sourceName: legacy.source_name,
    invoiceId: legacy.invoice_id,
    invoiceNumber: legacy.invoice_number,
    itemsCount: legacy.items_count ?? (legacy.items?.length ?? 0),
    totalQuantity: legacy.total_quantity ?? (legacy.items?.reduce((s, i) => s + i.quantity, 0) ?? 0),
    items: legacy.items?.map(mapDeliveryItemDTOToModel)
  };
};

const defaultByActionType = { delivery: 0, return: 0, adjustment: 0 };
const defaultBySourceType = { warehouse: 0, marketer: 0, provider: 0, system: 0 };

export const mapDeliverySummaryDTOToModel = (dto: StoreDeliverySummaryDTO): StoreDeliverySummary => ({
  totalDeliveries: dto.total_deliveries ?? 0,
  totalReturns: dto.total_returns ?? 0,
  totalAdjustments: dto.total_adjustments ?? 0,
  totalQuantityDelivered: dto.total_quantity_delivered ?? 0,
  totalQuantityReturned: dto.total_quantity_returned ?? 0,
  byActionType: dto.by_action_type
    ? {
        delivery: dto.by_action_type.delivery ?? 0,
        return: dto.by_action_type.return ?? 0,
        adjustment: dto.by_action_type.adjustment ?? 0
      }
    : defaultByActionType,
  bySourceType: dto.by_source_type
    ? {
        warehouse: dto.by_source_type.warehouse ?? 0,
        marketer: dto.by_source_type.marketer ?? 0,
        provider: dto.by_source_type.provider ?? 0,
        system: dto.by_source_type.system ?? 0
      }
    : defaultBySourceType,
  dateRange: dto.date_range ? {
    fromDate: dto.date_range.from_date,
    toDate: dto.date_range.to_date
  } : undefined
});

export const mapSummaryResponseToModel = (response: StoreDeliverySummaryResponseDTO): StoreDeliverySummary => {
  if (!response?.summary) {
    return {
      totalDeliveries: 0,
      totalReturns: 0,
      totalAdjustments: 0,
      totalQuantityDelivered: 0,
      totalQuantityReturned: 0,
      byActionType: defaultByActionType,
      bySourceType: defaultBySourceType
    };
  }
  return mapDeliverySummaryDTOToModel(response.summary);
};

function normalizePagination(p: StoreDeliveryTimelineResponseDTO['pagination']): {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
} {
  return {
    page: p.page ?? 1,
    limit: p.limit ?? 20,
    total: p.total ?? 0,
    total_pages: p.totalPages ?? p.total_pages ?? 1
  };
}

export const mapTimelineResponseToModel = (response: StoreDeliveryTimelineResponseDTO): {
  transactions: StoreDeliveryTransaction[];
  pagination: { page: number; limit: number; total: number; total_pages: number };
} => ({
  transactions: (response.transactions ?? []).map(mapDeliveryTransactionDTOToModel),
  pagination: normalizePagination(response.pagination)
});

/** Spec: GET /:id returns data = transaction object directly; legacy: data.transaction */
export const mapTransactionResponseToModel = (response: StoreDeliveryTransactionResponseDTO): StoreDeliveryTransaction => {
  const raw = response.transaction ?? response;
  if (raw && typeof raw === 'object' && ('id' in raw || 'transaction_id' in raw || 'created_at' in raw)) {
    return mapDeliveryTransactionDTOToModel(raw as AnyTransactionDTO);
  }
  return mapDeliveryTransactionDTOToModel(response as AnyTransactionDTO);
};
